import { Spinner } from "@app/shared/components/Spinner";
import { questionnairesKeys } from "@app/screens/ExternalQuestionnaires/data";
import { AiSuggestion } from "@app/shared/components/ai/AiSuggestion";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import {
    AnswerStatus,
    type GetQuestionsQuery,
} from "@generated/client/graphql";
import { useMutationState } from "@tanstack/react-query";
import { type FC } from "react";
import { useDeleteAiSuggestion, useGenerateAiSuggestion } from "../../data";
import { useQuestionsTabTranslations } from "../i18n";
import { EditSuggestionBlock } from "./EditSuggestionBlock";
import { useAiSuggestionConfirm } from "./useAiSuggestionConfirm";

export const AnswerAiSuggestionHeader: FC<{
    question: GetQuestionsQuery["questions"][number];
    locale: SupportedLocales;
}> = ({ question, locale }) => {
    const { t, keys } = useQuestionsTabTranslations();

    const aiSuggestion = question?.answer?.aiSuggestion;

    const aiSuggestionMutationState = useMutationState({
        filters: {
            mutationKey: questionnairesKeys.generateAiSuggestion(
                question.questionnaireId ?? undefined,
                question.id,
            ),
        },
        select: (mutation) => mutation.state.status,
    });

    const aiSubmitEditAnswerMutationState = useMutationState({
        filters: {
            predicate: (mutation) => {
                const key = mutation.options.mutationKey;

                if (!key || key.length < 2) return false;

                return (
                    key.includes("upsertAiSuggestion") &&
                    key.includes(question.id)
                );
            },
        },
        select: (mutation) => mutation.state.status,
    });
    const isSuggestPending =
        (!!aiSuggestionMutationState?.length &&
            aiSuggestionMutationState[aiSuggestionMutationState.length - 1] ===
                "pending") ||
        (question?.answer?.aiSuggestion?.isGenerating ?? false);

    const isSubmitPending =
        !!aiSubmitEditAnswerMutationState?.length &&
        aiSubmitEditAnswerMutationState[
            aiSubmitEditAnswerMutationState.length - 1
        ] === "pending";

    const isSuggestError = aiSuggestion?.success === false;

    const { generateAiSuggestion } = useGenerateAiSuggestion(
        question.questionnaireId ?? undefined,
        question.id,
    );
    const { deleteAiSuggestion } = useDeleteAiSuggestion();

    const handleSuggestButtonClick = () => {
        generateAiSuggestion({
            questionId: question.id,
        });
    };

    const handleIgnoreSuggestionClick = () => {
        deleteAiSuggestion(question.id);
    };

    const { handleConfirmClick } = useAiSuggestionConfirm({
        question,
        locale,
    });

    return (
        <AiSuggestion.Header
            title={
                isSuggestError
                    ? t(keys.ai_suggestion_error)
                    : t(keys.ai_suggestion_title)
            }
            isError={isSuggestError}
        >
            {isSuggestPending || isSubmitPending ? (
                <FlexRow
                    justifyContent="center"
                    alignItems="center"
                    w="7"
                    h="7"
                >
                    <Spinner className="border-t-accent-1-400 w-5 h-5" />
                </FlexRow>
            ) : (
                <FlexRow gap="2">
                    {isSuggestError ? (
                        <>
                            <AiSuggestion.Ignore
                                onClick={handleIgnoreSuggestionClick}
                                disabled={
                                    isSuggestPending ||
                                    question?.answer?.status ===
                                        AnswerStatus.Validated
                                }
                            >
                                {t(keys.ai_suggestion_ignore)}
                                <Button.Icon
                                    name="trash"
                                    size="sm"
                                    className="text-primary"
                                />
                            </AiSuggestion.Ignore>
                            <AiSuggestion.Confirm
                                onClick={handleSuggestButtonClick}
                                disabled={
                                    question?.answer?.status ===
                                    AnswerStatus.Validated
                                }
                            >
                                {t(keys.ai_suggestion_re_generate)}
                                <Button.Icon
                                    name="arrowCircleRight"
                                    size="sm"
                                />
                            </AiSuggestion.Confirm>
                        </>
                    ) : (
                        <Dropdown>
                            <Dropdown.Trigger>
                                <Button
                                    variant="tonal"
                                    size="sm"
                                    className="w-fit"
                                    disabled={
                                        isSuggestPending ||
                                        question?.answer?.status ===
                                            AnswerStatus.Validated
                                    }
                                >
                                    <Button.Icon
                                        name="more"
                                        size="sm"
                                        className="text-primary"
                                    />
                                </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content hasPortal>
                                <Dropdown.Item>
                                    <FlexRow
                                        gap="2"
                                        alignItems="center"
                                        onClick={handleSuggestButtonClick}
                                        className="cursor-pointer w-full"
                                    >
                                        <Icon
                                            name="arrowCircleRight"
                                            size="sm"
                                            className="text-accent-1-400"
                                        />
                                        {t(keys.ai_suggestion_re_generate)}
                                    </FlexRow>
                                </Dropdown.Item>
                                <Dropdown.Item>
                                    <EditSuggestionBlock question={question} />
                                </Dropdown.Item>
                                <Dropdown.Item>
                                    <FlexRow
                                        alignItems="center"
                                        gap="2"
                                        className="cursor-pointer w-full"
                                        onClick={handleConfirmClick}
                                    >
                                        <Icon
                                            name="check"
                                            size="sm"
                                            className="text-primary"
                                        />
                                        {t(keys.ai_suggestion_confirm_all)}
                                    </FlexRow>
                                </Dropdown.Item>
                                <Dropdown.Item>
                                    <FlexRow
                                        alignItems="center"
                                        gap="2"
                                        className="cursor-pointer w-full"
                                        onClick={handleIgnoreSuggestionClick}
                                    >
                                        <Icon
                                            name="trash"
                                            size="sm"
                                            className="text-primary"
                                        />
                                        {t(keys.ai_suggestion_ignore_all)}
                                    </FlexRow>
                                </Dropdown.Item>
                            </Dropdown.Content>
                        </Dropdown>
                    )}
                </FlexRow>
            )}
        </AiSuggestion.Header>
    );
};
